import { useState, useEffect, useCallback, useMemo } from "react";
import smartlyFavicon from "@/assets/smartly-favicon.png";
import smartlyMainLogo from "@/assets/smartly-main-logo.svg";
import { Menu, Phone, Rocket, Users as UsersIcon, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MegaMenu from "@/components/ui/mega-menu";
import type { MegaMenuItem } from "@/components/ui/mega-menu";
import MobileMenu from "@/components/MobileMenu";
import { services } from "@/data/services";
import { useContentMulti } from "@/hooks/useContent";
import { useMenuItems, type DbMenuItem } from "@/hooks/useMenuItems";
import { resolveIcon } from "@/lib/iconMap";

/* Static service group config for mega-menu columns */
const serviceGroups = [
  { title: "IT Management", slugs: ["managed-solutions-provider", "it-support", "vendor-management", "ai-integration"] },
  { title: "Security & Cloud", slugs: ["cyber-security", "cloud-solutions", "disaster-recovery"] },
  { title: "Development & Innovation", slugs: ["web-development", "mobile-app-development"] },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const c = useContentMulti({
    "nav.icon_url": "",
    "nav.icon_visible": "true",
    "nav.brand_name1": "Smartly",
    "nav.brand_name2": "Technologies",
    "nav.phone": "858-333-5005",
    "nav.phone_href": "8583335005",
    "nav.cta_label": "Get Started",
    "nav.cta_link": "/consultation",
    "nav.login_label": "Login",
    "nav.login_url": "https://support.smartly.net/",
  });

  const showIcon = c["nav.icon_visible"] !== "false";
  const iconUrl = c["nav.icon_url"] || smartlyFavicon;

  const { data: menuItems } = useMenuItems();

  /* Build desktop mega-menu items from DB */
  const megaMenuItems: MegaMenuItem[] = useMemo(() => {
    if (!menuItems || menuItems.length === 0) {
      // Fallback to hardcoded if DB hasn't loaded yet
      return [
        { id: 1, label: "Home", link: "/" },
        {
          id: 2,
          label: "About Us",
          link: "/about-us",
          subMenus: [
            {
              title: "Company",
              items: [
                { label: "Who We Are", description: "About our company", icon: UsersIcon, link: "/about-us" },
                { label: "Careers", description: "Join our team", icon: Briefcase, link: "/careers" },
              ],
            },
          ],
        },
        { id: 3, label: "Who We Help", link: "/industries" },
        {
          id: 4,
          label: "Services",
          subMenus: serviceGroups.map((group) => ({
            title: group.title,
            items: group.slugs.map((slug) => {
              const svc = services.find((s) => s.slug === slug)!;
              return { label: svc.title, description: svc.feature, icon: svc.icon, link: `/services/${svc.slug}` };
            }),
          })),
        },
        { id: 5, label: "Contact Us", link: "/contact" },
      ];
    }

    const topLevel = menuItems.filter((i) => !i.parent_id).sort((a, b) => a.sort_order - b.sort_order);
    const childrenOf = (parentId: string) =>
      menuItems.filter((i) => i.parent_id === parentId).sort((a, b) => a.sort_order - b.sort_order);

    return topLevel.map((item, idx) => {
      const kids = childrenOf(item.id);
      if (kids.length > 0) {
        // Detect whether this group's children are service pages
        const isServicesGroup = kids.some((k) => k.url.startsWith("/services/"));

        if (isServicesGroup) {
          // Build mega-menu sub-groups using explicit slug-to-group mapping
          const groupMap: Record<string, string> = {
            "managed-solutions-provider": "IT Management",
            "it-support": "IT Management",
            "vendor-management": "IT Management",
            "ai-integration": "IT Management",
            "cyber-security": "Security & Cloud",
            "cloud-solutions": "Security & Cloud",
            "disaster-recovery": "Security & Cloud",
            "web-development": "Development & Innovation",
            "mobile-app-development": "Development & Innovation",
          };
          const groupOrder = ["IT Management", "Security & Cloud", "Development & Innovation"];
          const grouped: Record<string, { label: string; description: string; icon: React.ElementType; link: string }[]> = {};
          for (const child of kids) {
            const slug = child.url.replace("/services/", "");
            const group = groupMap[slug] || "Other";
            if (!grouped[group]) grouped[group] = [];
            const svc = services.find((s) => `/services/${s.slug}` === child.url);
            grouped[group].push({
              label: child.label,
              description: svc?.feature || "",
              icon: svc?.icon || resolveIcon(child.icon || ""),
              link: child.url,
            });
          }
          const columns = groupOrder
            .filter((g) => grouped[g]?.length)
            .map((g) => ({ title: g, items: grouped[g] }));

          return { id: idx + 1, label: item.label, subMenus: columns } as MegaMenuItem;
        }

        // Generic dropdown (e.g. About Us > Careers): single column, parent stays linkable
        const items = kids.map((child) => ({
          label: child.label,
          description: child.description || "",
          icon: resolveIcon(child.icon || ""),
          link: child.url,
        }));
        const parentLink = item.url && item.url !== "#" ? item.url : undefined;
        return {
          id: idx + 1,
          label: item.label,
          ...(parentLink ? { link: parentLink } : {}),
          subMenus: [{ title: item.label, items }],
        } as MegaMenuItem;
      }

      return { id: idx + 1, label: item.label, link: item.url } as MegaMenuItem;
    });
  }, [menuItems]);

  /* Build mobile menu items from DB */
  const mobileTopItems: DbMenuItem[] = useMemo(() => {
    if (!menuItems) return [];
    return menuItems.filter((i) => !i.parent_id && i.url !== "#").sort((a, b) => a.sort_order - b.sort_order);
  }, [menuItems]);

  const servicesParent = useMemo(() => {
    if (!menuItems) return null;
    return menuItems.find((i) => !i.parent_id && i.url === "#");
  }, [menuItems]);

  const handleHomeClick = useCallback((e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMegaMenuClick = useCallback((link: string) => {
    if (link === "/" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(link);
    }
  }, [location.pathname, navigate]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 min-h-[64px] will-change-transform transition-all duration-300 ${
        scrolled ? "bg-black/95 backdrop-blur-md border-b-2 border-primary shadow-lg shadow-primary/10" : "bg-gradient-to-r from-black/80 via-[#1a0000]/60 to-black/80 backdrop-blur-sm"
      }`}
      style={{ transform: 'translateZ(0)' }}
    >
      <div className="container mx-auto flex items-center justify-between gap-3 h-16 px-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-1.5 sm:shrink-0 sm:gap-3">
          <Link to="/" className="flex min-w-0 items-center gap-1 sm:gap-1.5 whitespace-nowrap font-display text-base sm:text-xl font-bold tracking-tight">
            <img src={smartlyMainLogo} alt="Smartly logo" className="h-[clamp(18px,6vw,28px)] w-auto shrink-0 sm:h-6" />
            <span className="font-display text-[clamp(15px,5.2vw,22px)] font-bold tracking-tight text-primary-foreground sm:text-xl">{c["nav.brand_name2"]}</span>
          </Link>
          <span className="border-l border-border h-6 hidden sm:block" />
          <a
            href={`tel:${c["nav.phone_href"]}`}
            className="hidden sm:flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Phone className="h-4 w-4" />
            <span className="sm:hidden font-bold">Call Us</span>
            <span className="hidden sm:inline font-bold">{c["nav.phone"]}</span>
          </a>
        </div>

        {/* Desktop */}
        <div className="hidden xl:flex shrink-0 items-center gap-3 2xl:gap-4">
          <MegaMenu items={megaMenuItems} onItemClick={handleMegaMenuClick} />
          <a
            href={c["nav.login_url"]}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 whitespace-nowrap text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {c["nav.login_label"]}
          </a>
          <Button size="sm" asChild className="rounded-full">
            <Link to={c["nav.cta_link"] || "/consultation"}><Rocket className="h-4 w-4" /> {c["nav.cta_label"]}</Link>
          </Button>
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="xl:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu" className="h-11 w-11 shrink-0 text-primary-foreground bg-primary hover:bg-primary/90">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full max-w-none sm:max-w-none border-0 bg-background p-0 [&>button:last-child]:hidden"
          >
            <MobileMenu
              menuItems={menuItems}
              brandName={c["nav.brand_name2"]}
              phone={c["nav.phone"]}
              phoneHref={c["nav.phone_href"]}
              loginLabel={c["nav.login_label"]}
              loginUrl={c["nav.login_url"]}
              ctaLabel={c["nav.cta_label"]}
              ctaLink={c["nav.cta_link"]}
              onNavigate={() => setOpen(false)}
              onHomeClick={handleHomeClick}
            />
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default Navbar;
